// "Assign Course (Admin)" popup: pick category → subcategory (course) → content (one chapter, or the
// whole course), then whole Balgruha(s) or specific students, with an optional due date and notes.
import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { api } from '../../api';
import { resultDialog } from '../../utils/swalDialogs';
import { categoryIcon, categoryMeta } from './coursesShared';
import { useCourseThumbnail } from './courseThumbnails';

const WHOLE_COURSE = '__whole__';
const NOTES_MAX = 500;
const idOf = (v) => (v && typeof v === 'object' ? v._id || v.id : v) || '';
const listOf = (res, key) => {
  const d = res?.data?.data;
  const v = d?.[key] || d || res?.data?.[key];
  return Array.isArray(v) ? v : [];
};

// Item kinds shown on the content card (mockup: PDF, Audio, Video, Game/Activity, Quiz, Practice).
const KINDS = [
  { key: 'pdf', label: 'PDF', icon: '📄', unit: ['file', 'files'], types: ['pdf'] },
  { key: 'audio', label: 'Audio', icon: '🎧', unit: ['file', 'files'], types: ['audio'] },
  { key: 'video', label: 'Video', icon: '🎬', unit: ['file', 'files'], types: ['video'] },
  { key: 'game', label: 'Game/Activity', icon: '🎮', unit: ['activity', 'activities'], types: ['link'] },
  { key: 'quiz', label: 'Quiz', icon: '❓', unit: ['quiz', 'quizzes'], types: ['quiz'] },
  { key: 'practice', label: 'Practice', icon: '🎤', unit: ['activity', 'activities'], types: ['task'] },
  { key: 'other', label: 'Other', icon: '🖼️', unit: ['item', 'items'], types: ['image', 'text'] },
];

const itemsOf = (course, chapterId) => {
  const chapters = (course?.modules || []).flatMap((m) => m.chapters || []);
  const picked = chapterId && chapterId !== WHOLE_COURSE ? chapters.filter((c) => idOf(c) === chapterId) : chapters;
  return { chapters: picked, items: picked.flatMap((c) => c.contentItems || []) };
};
const coinsOf = (course, chapterId) => {
  const { chapters, items } = itemsOf(course, chapterId);
  const itemCoins = items.reduce((n, it) => n + (Number(it.coins) || 0), 0);
  const chapterCoins = chapters.reduce(
    (n, c) => n + (Number(c.defaultCoins) || 0) + (c.coinRules || []).reduce((k, r) => k + (Number(r.coins) || 0), 0),
    0
  );
  const completion = chapterId === WHOLE_COURSE && course?.enableCoinReward ? Number(course.coinsOnCompletion) || 0 : 0;
  return itemCoins + chapterCoins + completion;
};

function Picker({ label, required, value, onChange, options, placeholder, icon }) {
  return (
    <label className="ac-field">
      <span>{label}{required && <em> *</em>}</span>
      <span className="ac-select">
        {icon && <span className="ac-select__icon" aria-hidden="true">{icon}</span>}
        <select value={value} onChange={(e) => onChange(e.target.value)} disabled={!options.length}>
          <option value="">{options.length ? placeholder : 'None available'}</option>
          {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
        </select>
        <ChevronDown size={16} />
      </span>
    </label>
  );
}

export default function AssignCourseModal({ categories, courses, initialCourse, onClose, onAssigned }) {
  const published = useMemo(() => courses.filter((c) => c.status === 'published'), [courses]);
  const [category, setCategory] = useState(initialCourse?.category || '');
  const [courseId, setCourseId] = useState(initialCourse?._id || '');
  const [chapterId, setChapterId] = useState('');
  const [mode, setMode] = useState('balagruha');
  const [balagruhas, setBalagruhas] = useState([]);
  const [students, setStudents] = useState([]);
  const [pickedBalagruhas, setPickedBalagruhas] = useState([]);
  const [pickedStudents, setPickedStudents] = useState([]);
  const [bgQuery, setBgQuery] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  // Balgruhas + all students (paged like the existing assignment modal).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bgRes, first] = await Promise.all([
          api.get('/api/v1/balagruha'),
          api.get('/api/users', { params: { role: 'student', page: 1, limit: 1000 } }),
        ]);
        const pages = first.data?.pagination?.pages || 1;
        const rest = pages > 1
          ? await Promise.all(Array.from({ length: pages - 1 }, (_, i) => api.get('/api/users', { params: { role: 'student', page: i + 2, limit: 1000 } })))
          : [];
        if (cancelled) return;
        setBalagruhas(listOf(bgRes, 'balagruhas'));
        setStudents([...listOf(first, 'users'), ...rest.flatMap((r) => listOf(r, 'users'))].filter((s) => s?.role === 'student'));
      } catch {
        if (!cancelled) resultDialog('error', 'Could not load Balgruhas and students', 'Please close and try again.');
      } finally {
        if (!cancelled) setLoadingPeople(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Every main category is listed (plus any a published course still uses). Only published subcategories
  // can be assigned (the API refuses drafts), so drafts are shown greyed out with the reason.
  const categoryOptions = [...new Set([...categories.map((c) => c.name), ...published.map((c) => c.category)].filter(Boolean))]
    .map((name) => ({ value: name, label: name }));
  const courseOptions = [
    ...published.filter((c) => c.category === category).map((c) => ({ value: c._id, label: c.title })),
    ...courses
      .filter((c) => c.category === category && c.status === 'draft')
      .map((c) => ({ value: c._id, label: `${c.title} (draft – publish to assign)`, disabled: true })),
  ];
  const hasPublishedIn = courseOptions.some((o) => !o.disabled);
  const course = published.find((c) => c._id === courseId) || null;
  const chapters = (course?.modules || []).flatMap((m) => m.chapters || []);
  const contentOptions = [
    ...chapters.map((ch) => ({ value: idOf(ch), label: ch.title })),
    ...(chapters.length ? [{ value: WHOLE_COURSE, label: 'Entire course (all content)' }] : []),
  ];

  // Keep the selections consistent as parents change.
  useEffect(() => { if (course && course.category !== category) setCourseId(''); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!course) { setChapterId(''); return; }
    setChapterId((current) => (current && contentOptions.some((o) => o.value === current) ? current : contentOptions[0]?.value || ''));
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bgName = useMemo(() => Object.fromEntries(balagruhas.map((b) => [idOf(b), b.name])), [balagruhas]);
  const studentBalagruha = (s) => {
    const ids = [...(s.balagruhaIds || []), s.balagruhaId, s.balagruha].map(idOf).filter(Boolean);
    return ids.map((id) => bgName[id]).find(Boolean) || '';
  };
  const visibleBalagruhas = balagruhas.filter((b) => (b.name || '').toLowerCase().includes(bgQuery.trim().toLowerCase()));
  const visibleStudents = students
    .filter((s) => `${s.name || ''} ${studentBalagruha(s)}`.toLowerCase().includes(studentQuery.trim().toLowerCase()))
    .slice(0, 200);

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const selectedChapter = chapters.find((ch) => idOf(ch) === chapterId);
  const { items } = itemsOf(course, chapterId);
  const kindCounts = KINDS.map((k) => ({ ...k, count: items.filter((it) => k.types.includes(it.type)).length })).filter((k) => k.count || k.key !== 'other');
  const totalCoins = course ? coinsOf(course, chapterId) : 0;
  // Course lists come without thumbnails (they can be MBs each); the picked course's is loaded here.
  const courseThumbnail = useCourseThumbnail(course);
  const preview = selectedChapter?.thumbnail || courseThumbnail;
  const meta = categoryMeta(categories, category);

  const submit = async () => {
    if (!course) return resultDialog('error', 'Choose what to assign', 'Select a main category, subcategory and content.');
    if (!chapterId) return resultDialog('error', 'Choose the content', 'Pick a content item or "Entire course".');
    const targetIds = mode === 'balagruha' ? pickedBalagruhas : pickedStudents;
    if (!targetIds.length) return resultDialog('error', 'Choose who gets it', mode === 'balagruha' ? 'Select at least one Balgruha.' : 'Select at least one student.');
    setSaving(true);
    try {
      const res = await api.post('/api/v2/lms/admin/courses/assignments', {
        courseId: course._id,
        chapterId: chapterId === WHOLE_COURSE ? null : chapterId,
        assignedTo: mode === 'balagruha' ? { type: 'balagruha', balagruhaIds: pickedBalagruhas } : { type: 'students', studentIds: pickedStudents },
        dueDate: dueDate || null,
        notes: notes.trim(),
        notifications: { inApp: true, email: false },
      });
      const count = res.data?.data?.studentsAssigned;
      resultDialog('success', 'Course assigned', `${selectedChapter ? `"${selectedChapter.title}"` : `"${course.title}"`} was assigned${count ? ` to ${count} student${count > 1 ? 's' : ''}` : ''}.`);
      onAssigned();
    } catch (err) {
      resultDialog('error', 'Could not assign', err?.response?.data?.error || err?.response?.data?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ac-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="ac-modal" role="dialog" aria-modal="true" aria-labelledby="ac-title">
        <header className="ac-head">
          <div>
            <h2 id="ac-title">Assign Course (Admin)</h2>
            <p>Select category, subcategory, content and assign to students</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={saving}><X size={20} /></button>
        </header>

        <div className="ac-body">
          <section>
            <h3><span className="ac-step">1</span>Select Course Content</h3>
            <div className="ac-grid3">
              <Picker label="Main Category" required value={category} onChange={(v) => { setCategory(v); setCourseId(''); }} options={categoryOptions} placeholder="Select category" icon={categoryIcon(meta)} />
              <Picker label="Subcategory" required value={courseId} onChange={setCourseId} options={courseOptions} placeholder={category ? 'Select subcategory' : 'Pick a category first'} icon="🗂️" />
              <Picker label="Content" required value={chapterId} onChange={setChapterId} options={contentOptions} placeholder={course ? 'Select content' : 'Pick a subcategory first'} icon="📘" />
            </div>
            {!published.length && <p className="ac-hint">No published courses yet. Publish a course to assign it.</p>}
            {published.length > 0 && category && !hasPublishedIn && (
              <p className="ac-hint">{category} has no published subcategory yet. Publish one from Courses Management to assign it.</p>
            )}

            {course && chapterId && (
              <div className="ac-content">
                <span className="ac-content__thumb">
                  {preview ? <img src={preview} alt="" /> : <span aria-hidden="true">{course.icon || '📚'}</span>}
                </span>
                <div className="ac-content__info">
                  <b>{selectedChapter ? selectedChapter.title : `${course.title} (entire course)`}</b>
                  <small>Category: {course.category} <span aria-hidden="true">›</span> Subcategory: {course.title}</small>
                  <div className="ac-kinds">
                    {kindCounts.map((k) => (
                      <span key={k.key} className={k.count ? '' : 'is-zero'}>
                        <i aria-hidden="true">{k.icon}</i>
                        <b>{k.label}</b>
                        <small>{k.count} {k.unit[k.count === 1 ? 0 : 1]}</small>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="ac-coins">
                  <span aria-hidden="true">🪙</span>
                  <b>₹ {totalCoins}</b>
                  <small>Total Coins</small>
                </div>
              </div>
            )}
          </section>

          <section>
            <h3><span className="ac-step">2</span>Select Students</h3>
            <div className="ac-grid2">
              <div className={`ac-choice${mode === 'balagruha' ? ' is-on' : ''}`}>
                <label className="ac-radio">
                  <input type="radio" name="ac-mode" checked={mode === 'balagruha'} onChange={() => setMode('balagruha')} />
                  <span><b>Entire Balgruha(s)</b><small>All students in selected Balgruha(s) will receive this content.</small></span>
                </label>
                <div className="ac-list">
                  <div className="ac-list__bar">
                    <label className="ac-search"><Search size={14} /><input value={bgQuery} onChange={(e) => setBgQuery(e.target.value)} placeholder="Search Balgruha..." disabled={mode !== 'balagruha'} /></label>
                    <button type="button" disabled={mode !== 'balagruha'} onClick={() => setPickedBalagruhas([...new Set([...pickedBalagruhas, ...visibleBalagruhas.map(idOf)])])}>Select All</button>
                    <button type="button" disabled={mode !== 'balagruha'} onClick={() => setPickedBalagruhas([])}>Clear All</button>
                  </div>
                  <ul>
                    {loadingPeople && <li className="ac-list__empty">Loading…</li>}
                    {!loadingPeople && visibleBalagruhas.map((b) => {
                      const id = idOf(b);
                      const on = pickedBalagruhas.includes(id);
                      return (
                        <li key={id}>
                          <label className={mode !== 'balagruha' ? 'is-disabled' : ''}>
                            <input type="checkbox" checked={on} disabled={mode !== 'balagruha'} onChange={() => toggle(pickedBalagruhas, setPickedBalagruhas, id)} />
                            <span className="ac-check" aria-hidden="true">{on && <Check size={11} strokeWidth={3.5} />}</span>
                            {b.name}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className={`ac-choice${mode === 'students' ? ' is-on' : ''}`}>
                <label className="ac-radio">
                  <input type="radio" name="ac-mode" checked={mode === 'students'} onChange={() => setMode('students')} />
                  <span><b>Specific Students</b><small>Select individual students from any Balgruha.</small></span>
                </label>
                <div className="ac-list">
                  <div className="ac-list__bar">
                    <label className="ac-search ac-search--full"><Search size={14} /><input value={studentQuery} onChange={(e) => setStudentQuery(e.target.value)} placeholder="Search student name..." disabled={mode !== 'students'} /></label>
                  </div>
                  <ul>
                    {loadingPeople && <li className="ac-list__empty">Loading…</li>}
                    {!loadingPeople && visibleStudents.map((s) => {
                      const id = idOf(s);
                      const on = pickedStudents.includes(id);
                      const house = studentBalagruha(s);
                      return (
                        <li key={id}>
                          <label className={mode !== 'students' ? 'is-disabled' : ''}>
                            <input type="checkbox" checked={on} disabled={mode !== 'students'} onChange={() => toggle(pickedStudents, setPickedStudents, id)} />
                            <span className="ac-check" aria-hidden="true">{on && <Check size={11} strokeWidth={3.5} />}</span>
                            <span className="ac-avatar" aria-hidden="true">{(s.name || '?').charAt(0).toUpperCase()}</span>
                            {s.name}{house && <small> ({house})</small>}
                          </label>
                        </li>
                      );
                    })}
                    {!loadingPeople && !visibleStudents.length && <li className="ac-list__empty">No students match</li>}
                  </ul>
                </div>
              </div>
            </div>
            <p className="ac-hint">
              {mode === 'balagruha'
                ? `${pickedBalagruhas.length} Balgruha${pickedBalagruhas.length === 1 ? '' : 's'} selected`
                : `${pickedStudents.length} student${pickedStudents.length === 1 ? '' : 's'} selected`}
            </p>
          </section>

          <section>
            <h3><span className="ac-step">3</span>Additional Details</h3>
            <div className="ac-grid-details">
              <label className="ac-field">
                <span>Due Date (Optional)</span>
                <input type="date" value={dueDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDueDate(e.target.value)} />
              </label>
              <label className="ac-field">
                <span>Notes / Instructions (Optional)</span>
                <textarea value={notes} maxLength={NOTES_MAX} onChange={(e) => setNotes(e.target.value)} placeholder="Add any notes or instructions for students..." />
                <small className="ac-count">{notes.length}/{NOTES_MAX}</small>
              </label>
            </div>
          </section>
        </div>

        <footer className="ac-foot">
          <button type="button" className="cm-btn cm-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="cm-btn cm-btn--purple" onClick={submit} disabled={saving}>{saving ? 'Assigning…' : 'Assign Course'}</button>
        </footer>
      </div>
    </div>
  );
}
